
import Label from '../../../../../ui/label';
import { useDispatch } from 'react-redux';
import { addMultiplierRow, removeMultiplierRow, updateMultiplierValue, updateSalaryRankRow } from '../../../../../../features/gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.slice';
import { MaterialSymbolsDeleteRounded } from '../../../../../../assets/icon/iconify';
import { RowData } from '../../../../../../features/gms-proposal-forms/coverage/salary-rank/coverage-type-by-salary-rank.state';
import Input from '../../../../../ui/input';
import { customStyle, defaultStyle } from '../../../../../../constant/custom-style';
import { useProposalTypeData } from '../../../../../../hooks/proposal/use-proposal-type-data';

export default function BySalaryRank() {
    const dispatch = useDispatch();
    const { coverage, inputsBySalaryRank } = useProposalTypeData();

    const handleSalaryChange = (
        id: string,
        value: string,
        type: 'aveSalary' | 'salMultiplier' | 'salMultiplierLabel',
        row: RowData
    ) => {
        if (type === 'aveSalary') {
            let raw = value.replace(/[^0-9.]/g, '');
            const parts = raw.split('.');
            if (parts.length > 2) raw = parts[0] + '.' + parts[1];

            const [integerPart, decimalPart] = raw.split('.');
            if (decimalPart) {
                raw = `${integerPart}.${decimalPart.slice(0, 2)}`;
            }

            dispatch(updateSalaryRankRow({ id, field: 'salary', value: raw }));
            return;
        }

        if (type === 'salMultiplier') {
            const raw = value.replace(/\D/g, '');

            if (raw === '') {
                dispatch(updateMultiplierValue({ id, value: '' }));
                return;
            }

            dispatch(updateSalaryRankRow({ id, field: 'multiplierLabel', value: raw }));
            dispatch(updateMultiplierValue({ id, value: raw }));
        }
    };

    const handleDesignationChange = (id: string, value: string) => {
        dispatch(updateSalaryRankRow({ id, field: 'designation', value }));
    };

    const formatAmount = (num: number) =>
        num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const hasInvalidMultiplier = inputsBySalaryRank.some(
        row => row.multiplierValue < 12 || row.multiplierValue > 48
    );

    const canRemoveRow = inputsBySalaryRank.length > 2;

    const getInputClass = (value: string | number) =>
        `${defaultStyle} w-full min-w-0 ${value !== '' && value !== 0 ? customStyle : ''}`;

    return (
        <div className="my-2 min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
            {/* HEADER */}
            <div className="flex flex-row items-center justify-between gap-2 border-b border-[#186cb2]/20 bg-[#186cb2]/10 px-3 py-3 sm:px-4">
                <div className="min-w-0 flex-1">
                    <Label
                        label={coverage?.name ?? ''}
                        fontWeight="font-semibold text-gray-800"
                        fontSize="text-xs"
                    />
                    <p className="mt-1 hidden text-[10px] leading-4 text-gray-500 sm:block sm:text-[11px]">
                        Enter the average salary, multiplier, and designation for each salary rank.
                    </p>
                    <p className="mt-1 text-[10px] text-gray-400">
                        {inputsBySalaryRank.length} salary ranks
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => dispatch(addMultiplierRow())}
                    aria-label="Add salary rank"
                    title="Add salary rank"
                    className="inline-flex size-9 shrink-0 items-center justify-center gap-1.5 rounded-md border border-[#186cb2] bg-[#186cb2] text-xs font-medium text-white transition-colors hover:bg-[#145d99] sm:w-auto sm:px-4"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14" />
                        <path d="M5 12h14" />
                    </svg>
                    <span className="hidden sm:inline">Add row</span>
                </button>
            </div>

            {/* RESPONSIVE TABLE — SAME TABLE ON ALL DEVICES */}
            <div className="min-w-0 bg-white">
                <div className="overflow-x-auto overscroll-x-contain">
                    <table className="w-full min-w-[760px] border-collapse text-xs sm:min-w-[820px] lg:min-w-0 lg:table-fixed">
                        <thead>
                            <tr className="bg-gray-100 text-gray-800">
                                <th className="w-[16%] border px-2 py-2.5 text-left text-[10px] font-semibold sm:px-3 sm:text-xs lg:w-[15%]">
                                    Salary Multiplier
                                </th>
                                <th className="w-[20%] border px-2 py-2.5 text-left text-[10px] font-semibold sm:px-3 sm:text-xs lg:w-[20%]">
                                    Average Salary
                                </th>
                                <th className="w-[20%] border px-2 py-2.5 text-left text-[10px] font-semibold sm:px-3 sm:text-xs lg:w-[20%]">
                                    Computation
                                </th>
                                <th className="w-[30%] border px-2 py-2.5 text-left text-[10px] font-semibold sm:px-3 sm:text-xs lg:w-[35%]">
                                    Designation
                                </th>
                                <th className="w-14 border px-1 py-2.5 text-center text-[10px] font-semibold sm:w-16 sm:px-2 sm:text-xs">
                                    Action
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {inputsBySalaryRank.map((row, idx) => (
                                <tr key={row.id} className="transition-colors hover:bg-gray-50">
                                    {/* SALARY MULTIPLIER */}
                                    <td className="border px-2 py-2 sm:px-3">
                                        <div className="flex min-w-0 items-center gap-1.5">
                                            <span className="shrink-0 text-xs text-gray-600">X</span>
                                            <Input
                                                type="number"
                                                name={`salaryMultiplier-${row.id}`}
                                                min={12}
                                                max={48}
                                                inputMode="numeric"
                                                value={String(row.multiplierValue)}
                                                isCustomStyle={true}
                                                customCss={getInputClass(row.multiplierValue)}
                                                onChange={(e) =>
                                                    handleSalaryChange(row.id, e.target.value, 'salMultiplier', row)
                                                }
                                                placeholder="12–48"
                                                required={true}
                                            />
                                        </div>
                                    </td>

                                    {/* AVERAGE SALARY */}
                                    <td className="border px-2 py-2 sm:px-3">
                                        <Input
                                            type="text"
                                            name={`averageSalary-${row.id}`}
                                            inputMode="decimal"
                                            value={row.salary ? String(row.salary) : ''}
                                            isCustomStyle={true}
                                            customCss={getInputClass(row.salary)}
                                            onChange={(e) =>
                                                handleSalaryChange(row.id, e.target.value, 'aveSalary', row)
                                            }
                                            placeholder="Enter salary"
                                            required={true}
                                        />
                                    </td>

                                    {/* COMPUTATION */}
                                    <td className="border px-2 py-2 sm:px-3">
                                        <Input
                                            type="text"
                                            name={`computation-${row.id}`}
                                            value={formatAmount(row.result)}
                                            placeholder="Computation"
                                            isCustomStyle={true}
                                            customCss={`${defaultStyle} w-full min-w-0 bg-gray-100`}
                                            disabled={true}
                                        />
                                    </td>

                                    {/* DESIGNATION */}
                                    <td className="border px-2 py-2 sm:px-3">
                                        <Input
                                            type="text"
                                            name={`designation-${row.id}`}
                                            placeholder={
                                                !row.multiplierLabel
                                                    ? 'Enter designation'
                                                    : `Designation for ${row.multiplierLabel}`
                                            }
                                            isCustomStyle={true}
                                            customCss={getInputClass(row.designation || '')}
                                            value={row.designation || ''}
                                            onChange={(e) =>
                                                handleDesignationChange(row.id, e.target.value)
                                            }
                                            required={true}
                                        />
                                    </td>

                                    {/* ACTION */}
                                    <td className="border px-1 py-2 text-center sm:px-2">
                                        <button
                                            type="button"
                                            onClick={() => dispatch(removeMultiplierRow(row.id))}
                                            disabled={!canRemoveRow}
                                            aria-label={`Remove salary rank ${idx + 1}`}
                                            title="Remove row"
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-500 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 sm:h-9 sm:w-9"
                                        >
                                            <MaterialSymbolsDeleteRounded
                                                color="#ef4444"
                                                height={20}
                                                width={20}
                                            />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* TABLE FOOTER */}
                <div className="flex flex-col gap-1 border-t border-gray-100 bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[10px] text-gray-500">
                        {inputsBySalaryRank.length} salary ranks
                    </p>
                    <p className="text-[10px] text-gray-400">
                        Swipe horizontally to view all columns on smaller screens.
                    </p>
                </div>

                {hasInvalidMultiplier && (
                    <p className="m-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">
                        Salary multiplier must be between 12 and 48.
                    </p>
                )}
            </div>
        </div>
    );
}
